import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "sr-only" }

import { ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sun, Moon, Monitor } from 'lucide-vue-next'

type Theme = 'light' | 'dark' | 'system'


export default /*@__PURE__*/_defineComponent({
  __name: 'ThemeToggle',
  setup(__props) {

const { t } = useI18n()
const { user, updatePreferences } = useAuth()

const currentTheme = ref<Theme>('system')

const getSystemTheme = (): 'light' | 'dark' => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

const applyTheme = (theme: Theme) => {
  const root = document.documentElement
  const effectiveTheme = theme === 'system' ? getSystemTheme() : theme

  if (effectiveTheme === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

const setTheme = async (theme: Theme) => {
  currentTheme.value = theme
  applyTheme(theme)
  localStorage.setItem('theme', theme)

  // Update user preferences if logged in
  if (user.value && theme !== 'system') {
    try {
      await updatePreferences({ theme })
    } catch {
      // Silently fail - theme will still work locally
    }
  }
}

// Initialize theme
onMounted(() => {
  const savedTheme = localStorage.getItem('theme') as Theme | null
  const userTheme = user.value?.theme

  if (savedTheme) {
    currentTheme.value = savedTheme
  } else if (userTheme) {
    currentTheme.value = userTheme
  }

  applyTheme(currentTheme.value)

  // Listen for system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (currentTheme.value === 'system') {
      applyTheme('system')
    }
  })
})

// Watch for user changes (after login)
watch(
  () => user.value?.theme,
  (newTheme) => {
    if (newTheme && !localStorage.getItem('theme')) {
      currentTheme.value = newTheme
      applyTheme(newTheme)
    }
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(DropdownMenu), null, {
    default: _withCtx(() => [
      _createVNode(_unref(DropdownMenuTrigger), { "as-child": "" }, {
        default: _withCtx(() => [
          _createVNode(_unref(Button), {
            variant: "ghost",
            size: "icon"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Sun), { class: "h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" }),
              _createVNode(_unref(Moon), { class: "absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" }),
              _createElementVNode("span", _hoisted_1, _toDisplayString(_unref(t)('settings.theme.title')), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_unref(DropdownMenuContent), { align: "end" }, {
        default: _withCtx(() => [
          _createVNode(_unref(DropdownMenuItem), {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (setTheme('light')))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Sun), { class: "mr-2 h-4 w-4" }),
              _createTextVNode(" " + _toDisplayString(_unref(t)('settings.theme.light')), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(DropdownMenuItem), {
            onClick: _cache[1] || (_cache[1] = ($event: any) => (setTheme('dark')))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Moon), { class: "mr-2 h-4 w-4" }),
              _createTextVNode(" " + _toDisplayString(_unref(t)('settings.theme.dark')), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(DropdownMenuItem), {
            onClick: _cache[2] || (_cache[2] = ($event: any) => (setTheme('system')))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Monitor), { class: "mr-2 h-4 w-4" }),
              _createTextVNode(" " + _toDisplayString(_unref(t)('settings.theme.system')), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})